"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.appBridgeHostMessageSchema = exports.appBridgeClientMessageSchema = exports.appBridgeMessageIdentitySchema = exports.appBridgeContextSchema = exports.appBridgeTargetSchema = exports.appBridgeResourceTypeSchema = exports.appBridgeBootstrapSchema = exports.APP_BRIDGE_PROTOCOL_VERSION = void 0;
exports.parseAppBridgeClientMessage = parseAppBridgeClientMessage;
exports.parseAppBridgeHostMessage = parseAppBridgeHostMessage;
exports.isSupportedAppBridgeProtocol = isSupportedAppBridgeProtocol;
const zod_1 = require("zod");
const app_sdk_1 = require("@crewfsm/app-sdk");
exports.APP_BRIDGE_PROTOCOL_VERSION = "1.0";
exports.appBridgeBootstrapSchema = zod_1.z.object({
    kind: zod_1.z.literal("CREWFSM_APP_BOOTSTRAP"),
    protocolVersion: zod_1.z.literal(exports.APP_BRIDGE_PROTOCOL_VERSION),
    installationId: zod_1.z.string().uuid(),
});
exports.appBridgeResourceTypeSchema = zod_1.z.enum([
    "DASHBOARD",
    "LEAD",
    "JOB",
    "CUSTOMER",
    "ESTIMATE",
    "INVOICE",
    "SETTINGS",
]);
exports.appBridgeTargetSchema = app_sdk_1.appUiTargetSchema;
exports.appBridgeContextSchema = zod_1.z.object({
    locationId: zod_1.z.string().uuid().optional(),
    target: exports.appBridgeTargetSchema,
    resourceType: exports.appBridgeResourceTypeSchema.optional(),
    resourceId: zod_1.z.string().uuid().optional(),
    locale: zod_1.z.string().min(2).max(35),
    timezone: zod_1.z.string().min(1).max(80),
    theme: zod_1.z.enum(["LIGHT", "DARK", "SYSTEM"]),
    themeContract: app_sdk_1.appThemeContractSchema,
    effectivePermissions: zod_1.z.array(zod_1.z.string().min(3).max(120)),
    currentRoute: safeRelativePathSchema(),
});
const commonEnvelopeSchema = zod_1.z.object({
    protocolVersion: zod_1.z.literal(exports.APP_BRIDGE_PROTOCOL_VERSION),
    requestId: zod_1.z.string().uuid(),
    installationId: zod_1.z.string().uuid(),
    timestamp: zod_1.z.string().datetime({ offset: true }),
});
exports.appBridgeMessageIdentitySchema = zod_1.z.object({
    protocolVersion: zod_1.z.string().min(1).max(20),
    requestId: zod_1.z.string().uuid(),
    installationId: zod_1.z.string().uuid(),
});
const readyMessageSchema = commonEnvelopeSchema.extend({
    type: zod_1.z.literal("CREWFSM_APP_READY"),
    payload: zod_1.z.object({
        clientVersion: zod_1.z.string().regex(/^\d+\.\d+\.\d+$/),
        currentRoute: safeRelativePathSchema(),
    }),
});
const navigateMessageSchema = commonEnvelopeSchema.extend({
    type: zod_1.z.literal("CREWFSM_NAVIGATE"),
    payload: zod_1.z.object({ path: safeRelativePathSchema() }),
});
const openResourceMessageSchema = commonEnvelopeSchema.extend({
    type: zod_1.z.literal("CREWFSM_OPEN_RESOURCE"),
    payload: zod_1.z.object({
        resourceType: exports.appBridgeResourceTypeSchema,
        resourceId: zod_1.z.string().uuid(),
    }),
});
const setTitleMessageSchema = commonEnvelopeSchema.extend({
    type: zod_1.z.literal("CREWFSM_SET_TITLE"),
    payload: zod_1.z.object({ title: zod_1.z.string().trim().min(1).max(120) }),
});
const setLoadingMessageSchema = commonEnvelopeSchema.extend({
    type: zod_1.z.literal("CREWFSM_SET_LOADING"),
    payload: zod_1.z.object({ loading: zod_1.z.boolean() }),
});
const showToastMessageSchema = commonEnvelopeSchema.extend({
    type: zod_1.z.literal("CREWFSM_SHOW_TOAST"),
    payload: zod_1.z.object({
        message: zod_1.z.string().trim().min(1).max(240),
        tone: zod_1.z.enum(["INFO", "SUCCESS", "WARNING", "CRITICAL"]),
    }),
});
const openModalMessageSchema = commonEnvelopeSchema.extend({
    type: zod_1.z.literal("CREWFSM_OPEN_MODAL"),
    payload: zod_1.z.object({ modalId: zod_1.z.string().min(2).max(80) }),
});
const closeModalMessageSchema = commonEnvelopeSchema.extend({
    type: zod_1.z.literal("CREWFSM_CLOSE_MODAL"),
    payload: zod_1.z.object({ modalId: zod_1.z.string().min(2).max(80).optional() }),
});
const requestResizeMessageSchema = commonEnvelopeSchema.extend({
    type: zod_1.z.literal("CREWFSM_REQUEST_RESIZE"),
    payload: zod_1.z.object({ height: zod_1.z.number().int().min(48).max(1_200) }),
});
const refreshContextMessageSchema = commonEnvelopeSchema.extend({
    type: zod_1.z.literal("CREWFSM_REFRESH_CONTEXT"),
    payload: zod_1.z.object({}),
});
exports.appBridgeClientMessageSchema = zod_1.z.discriminatedUnion("type", [
    readyMessageSchema,
    navigateMessageSchema,
    openResourceMessageSchema,
    setTitleMessageSchema,
    setLoadingMessageSchema,
    showToastMessageSchema,
    openModalMessageSchema,
    closeModalMessageSchema,
    requestResizeMessageSchema,
    refreshContextMessageSchema,
]);
const contextMessageSchema = commonEnvelopeSchema.extend({
    type: zod_1.z.literal("CREWFSM_CONTEXT"),
    payload: zod_1.z.object({
        context: exports.appBridgeContextSchema,
        sessionToken: zod_1.z.string().min(32),
        expiresAt: zod_1.z.string().datetime({ offset: true }),
    }),
});
const acknowledgementMessageSchema = commonEnvelopeSchema.extend({
    type: zod_1.z.literal("CREWFSM_ACK"),
    payload: zod_1.z.object({ acknowledgedType: zod_1.z.string().min(3).max(80) }),
});
const routeChangedMessageSchema = commonEnvelopeSchema.extend({
    type: zod_1.z.literal("CREWFSM_ROUTE_CHANGED"),
    payload: zod_1.z.object({ currentRoute: safeRelativePathSchema() }),
});
const errorMessageSchema = commonEnvelopeSchema.extend({
    type: zod_1.z.literal("CREWFSM_ERROR"),
    payload: zod_1.z.object({
        code: zod_1.z.enum([
            "INVALID_MESSAGE",
            "INVALID_ORIGIN",
            "INVALID_SOURCE",
            "INSTALLATION_MISMATCH",
            "INSTALLATION_INACTIVE",
            "PERMISSION_DENIED",
            "UNSUPPORTED_PROTOCOL",
            "SESSION_REQUIRED",
            "COMMAND_FAILED",
            "TIMEOUT",
        ]),
        message: zod_1.z.string().trim().min(1).max(240),
    }),
});
exports.appBridgeHostMessageSchema = zod_1.z.discriminatedUnion("type", [
    contextMessageSchema,
    acknowledgementMessageSchema,
    routeChangedMessageSchema,
    errorMessageSchema,
]);
function parseAppBridgeClientMessage(input) {
    return exports.appBridgeClientMessageSchema.parse(input);
}
function parseAppBridgeHostMessage(input) {
    return exports.appBridgeHostMessageSchema.parse(input);
}
function isSupportedAppBridgeProtocol(input) {
    if (!input || typeof input !== "object")
        return false;
    return ("protocolVersion" in input &&
        input.protocolVersion === exports.APP_BRIDGE_PROTOCOL_VERSION);
}
function safeRelativePathSchema() {
    return zod_1.z
        .string()
        .min(1)
        .max(240)
        .refine((value) => value.startsWith("/") &&
        !value.startsWith("//") &&
        !value.includes("\\") &&
        !value.split("/").includes(".."), "A safe app-relative path is required.");
}
//# sourceMappingURL=protocol.js.map